import React, { useEffect, useState } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { Configuration, DataReportsApi, ReportLanguage } from '@dialogue/scribe'
import { Box, CircularProgress } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { getApplicationConfig } from 'config'

import { useLanguage } from '../hooks/use-language'

export interface DataReportProps {
  organizationIds: string[]
}

export const DataReport: React.FC<DataReportProps> = ({ organizationIds }) => {
  const { getAccessTokenSilently } = useAuth0()
  const language = useLanguage()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [reportIframeIsLoading, setReportIframeIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (organizationIds.length === 0) {
        return
      }

      try {
        const token = await getAccessTokenSilently()
        const { scribe } = getApplicationConfig()

        const configuration = new Configuration({
          basePath: scribe.baseUrl,
          accessToken: token,
        })

        const dataReportsApi = new DataReportsApi(configuration)

        const response = await dataReportsApi.aggregatedDataReportSignedUrl(
          organizationIds.map((id) => parseInt(id)),
          language as ReportLanguage,
        )

        setReportIframeIsLoading(true)
        setSignedUrl(response.data.data.report_url)
      } catch (error) {
        console.error('Failed to fetch data report signed URL')
        enqueueSnackbar(t('organizationReportsPage.errors.fetchSignedUrl'), { variant: 'error' })
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchSignedUrl()
      }
    }

    void fetchSignedUrl()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [getAccessTokenSilently, organizationIds, language, enqueueSnackbar, t])

  if (!signedUrl) {
    return null
  }

  return (
    <>
      {reportIframeIsLoading && (
        <Box
          sx={{
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            top: '50%',
            left: '50%',
            zIndex: 1,
          }}
        >
          <CircularProgress size={100} />
        </Box>
      )}
      <iframe
        data-testid="evidence-data-report-container"
        src={signedUrl}
        title="Data Report"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        onLoad={() => setReportIframeIsLoading(false)}
        allowFullScreen
      />
    </>
  )
}
